import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../css/Home.css';
import usePageTitle from '../hooks/usePageTitle';
import { API_BASE_URL } from '../helpers/api';
import { truncateText, renderHTML } from '../hooks/tools';
import { getEstablishments } from '../helpers/establishments';
import { getReviews } from '../helpers/reviews';
import SearchModal from '../components/Search';
import { getCategories } from '../helpers/categories';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { getEvents } from '../helpers/events';

const Home = () => {
  usePageTitle('Accueil');
  const navigate = useNavigate();
  const [establishments, setEstablishments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [establishmentsData, reviewsData, catData, eventsData] = await Promise.all([
        getEstablishments(),
        getReviews(),
        getCategories(),
        getEvents()
      ]);

      // Create a map of establishment stats
      const reviewStats = {};
      reviewsData?.forEach(review => {
        if (!review?.establishment?.documentId) return;

        const estId = review.establishment.documentId;
        if (!reviewStats[estId]) {
          reviewStats[estId] = {
            count: 0,
            sum: 0
          };
        }
        reviewStats[estId].count++;
        reviewStats[estId].sum += review.rating || 0;
      });

      // Merge stats with establishments
      const establishmentsWithStats = establishmentsData.map(establishment => ({
        ...establishment,
        reviewStats: reviewStats[establishment.documentId]
          ? {
            count: reviewStats[establishment.documentId].count,
            average: parseFloat((reviewStats[establishment.documentId].sum /
              reviewStats[establishment.documentId].count).toFixed(1))
          }
          : { count: 0, average: 0 }
      }));

      // Filter and sort upcoming events
      const now = new Date();
      const upcomingEvents = eventsData
        .filter(event => new Date(event.startTime) > now)
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
        .slice(0, 4); // Only take the next 4 events

      setEvents(upcomingEvents);
      setEstablishments(establishmentsWithStats);
      setCategories(catData);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/les-spots?category=${categoryId}`);
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="home">
      <div className="hero-section">
        <div className="hero-content">
          <h1>Découvrez les meilleurs spots de la Guadeloupe</h1>
          <p>Des restaurants traditionnels aux bars de plage, trouvez les lieux qui vous correspondent</p>
          <div className="search-container">
            <button className="search-btn" onClick={() => setIsSearchOpen(true)}>
              <i className="bi bi-search"></i>
              Rechercher
            </button>
          </div>
        </div>
      </div>

      {/* Categories section - Now clickable */}
      <div className="categories-section">
        <div className="container">
          <div className="categories-grid">
            {categories.slice(0, 4).map((category, index) => (
              <div
                key={index}
                className="category-card"
                onClick={() => handleCategoryClick(category.id)}
                style={{ cursor: 'pointer' }}
              >
                <ion-icon name={category.icon} style={{
                  fontSize: "32px",
                  color: "#008b2c"
                }}></ion-icon>
                <span>{category.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Spots section */}
      <div className="container">
        <div className="section-header">
          <h2>Les Spots</h2>
          <Link to="/les-spots" className="view-all">Voir tout <i className="bi bi-arrow-right"></i></Link>
        </div>

        <div className="establishments-grid">
          {establishments?.length > 0 ? (
            establishments.slice(0, 4).map((establishment) => (
              <Link
                to={`/spot/${establishment.documentId}`}
                key={establishment.id}
                className="establishment-card"
              >
                <div className="card-image">
                  <img
                    src={API_BASE_URL + establishment.images[0].url}
                    alt={establishment.name}
                  />
                  <span className="category-badge">
                    {establishment.category.name}
                  </span>
                </div>
                <div className="card-content">
                  <div className="card-header">
                    <h3>{establishment.name}</h3>
                    <span className="price">{establishment.price}</span>
                  </div>
                  <div className="description">{renderHTML(establishment.description, true, 60)}</div>
                  <div className="card-meta">
                    <span className="rating">
                      <i className="bi bi-star-fill"></i>
                      {establishment.reviewStats.average || 0}
                      <small className="review-count">
                        ({establishment.reviewStats.count})
                      </small>
                    </span>
                    <span className="location">
                      <i className="bi bi-geo-alt"></i>
                      {establishment.city}
                    </span>
                  </div>
                  <div className="tags">
                    {establishment.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="tag">{tag.name}</span>
                    ))}
                    {establishment.tags.length > 3 && (
                      <span className="tag-more">+{establishment.tags.length - 3}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <p>Aucun établissement pour le moment</p>
          )}
        </div>
      </div>

      {/* Upcoming Events Section */}
      <div className="container">
        {events.length > 0 && (
          <>
            <div className="section-header">
              <h2>Événements à venir</h2>
              <Link to="/evenements" className="view-all">
                Voir tout <i className="bi bi-arrow-right"></i>
              </Link>
            </div>

            <div className="events-grid">
              {events.map((event) => (
                <Link
                  to={`/evenement/${event.documentId}`}
                  key={event.id}
                  className="event-card"
                >
                  <div className="card-image">
                    <img
                      src={event.images[0]?.url
                        ? API_BASE_URL + event.images[0].url
                        : '/placeholder.jpg'}
                      alt={event.name}
                    />
                    <div className="event-date">
                      <Calendar size={16} />
                      {new Intl.DateTimeFormat('fr-FR', {
                        day: 'numeric',
                        month: 'long'
                      }).format(new Date(event.startTime))}
                    </div>
                    <div className="event-time">
                      <Clock size={16} />
                      {new Intl.DateTimeFormat('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      }).format(new Date(event.startTime))}
                    </div>
                  </div>
                  <div className="card-content">
                    <div className="card-header">
                      <h3>{event.name}</h3>
                    </div>
                    <p className="description">
                      {renderHTML(event.description, true, 120)}
                    </p>
                    <div className="card-meta">
                      {event.venueName && (
                        <span className="venue">
                          <MapPin size={16} />
                          {event.venueName}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </div>
  );
};

export default Home;